(function () {
  const ALLOWED_MIMES = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_IMAGES = 5;
  const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  const TOTAL_STEPS = 3;

  function init() {
    const configEl = document.querySelector('script[id^="concept-submit-config"]');
    if (!configEl) return;

    let config;
    try {
      config = JSON.parse(configEl.textContent || '{}');
    } catch {
      return;
    }

    const root = configEl.nextElementSibling?.closest?.('section') || configEl.parentElement;
    const form = (root || document).querySelector('[data-concept-form]');
    if (!form) return;

    const progressEl = form.querySelector('[data-concept-progress]');
    const backBtn = form.querySelector('[data-concept-back]');
    const continueBtn = form.querySelector('[data-concept-continue]');
    const submitBtn = form.querySelector('[data-concept-submit]');
    const successEl = (root || document).querySelector('[data-concept-result]');
    const formErrorEl = form.querySelector('[data-form-error]');
    const fileInput = form.querySelector('input[name="images"]');
    const dropzoneEl = form.querySelector('[data-image-dropzone]');
    const emptyEl = form.querySelector('[data-image-empty]');
    const previewsEl = form.querySelector('[data-image-previews]');
    const submitSpinner = form.querySelector('[data-submit-spinner]');
    const submitLabel = form.querySelector('[data-submit-label]');
    const resultIconSuccess = successEl?.querySelector('[data-result-icon-success]');
    const resultIconError = successEl?.querySelector('[data-result-icon-error]');
    const resultTitle = successEl?.querySelector('[data-result-title]');
    const resultMessage = successEl?.querySelector('[data-result-message]');

    let step = 1;
    let images = [];
    let previewUrls = [];
    let dragDepth = 0;

    function fieldValue(name) {
      const el = form.querySelector(`[name="${name}"]`);
      return el ? String(el.value || '').trim() : '';
    }

    function setError(name, message) {
      const el = form.querySelector(`[data-error-for="${name}"]`);
      if (!el) return;
      if (message) {
        el.textContent = message;
        el.classList.remove('hidden');
      } else {
        el.textContent = '';
        el.classList.add('hidden');
      }
    }

    function clearErrors() {
      form.querySelectorAll('[data-error-for]').forEach((el) => {
        el.textContent = '';
        el.classList.add('hidden');
      });
      if (formErrorEl) {
        formErrorEl.textContent = '';
        formErrorEl.classList.add('hidden');
      }
    }

    function revokePreviewUrls() {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
      previewUrls = [];
    }

    function loadImageDimensions(file) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Could not read image.'));
        };
        img.src = url;
      });
    }

    async function addImageFiles(fileList) {
      let rejectedType = false;
      let rejectedSize = false;
      let rejectedShape = false;
      const candidates = Array.from(fileList || []).filter((file) => {
        if (!ALLOWED_MIMES.includes(file.type)) {
          rejectedType = true;
          return false;
        }
        if (file.size > MAX_IMAGE_BYTES) {
          rejectedSize = true;
          return false;
        }
        return true;
      });

      const incoming = [];
      for (const file of candidates) {
        try {
          const { width, height } = await loadImageDimensions(file);
          if (!width || !height || width !== height) {
            rejectedShape = true;
            continue;
          }
          incoming.push(file);
        } catch {
          rejectedShape = true;
        }
      }

      if (rejectedType) setError('images', 'Images must be jpeg, png, or webp.');
      else if (rejectedSize) setError('images', 'Each image must be 5MB or smaller.');
      else if (rejectedShape) setError('images', 'Images must be square.');
      else setError('images', '');

      if (!incoming.length) return;
      images = [...images, ...incoming].slice(0, MAX_IMAGES);
      renderPreviews();
    }

    function setDropzoneActive(active) {
      if (!dropzoneEl) return;
      dropzoneEl.classList.toggle('border-primary-light', active);
      dropzoneEl.classList.toggle('bg-primary-lightest', active);
      if (!active) {
        dropzoneEl.classList.add('border-gray-300');
        dropzoneEl.classList.toggle('bg-gray-50', images.length === 0);
        dropzoneEl.classList.toggle('bg-white', images.length > 0);
      }
    }

    async function validateImages() {
      if (images.length < 1 || images.length > MAX_IMAGES) {
        setError('images', 'Add between 1 and 5 images.');
        return false;
      }
      for (const file of images) {
        if (!ALLOWED_MIMES.includes(file.type)) {
          setError('images', 'Images must be jpeg, png, or webp.');
          return false;
        }
        if (file.size > MAX_IMAGE_BYTES) {
          setError('images', 'Each image must be 5MB or smaller.');
          return false;
        }
        try {
          const { width, height } = await loadImageDimensions(file);
          if (!width || !height || width !== height) {
            setError('images', 'Images must be square.');
            return false;
          }
        } catch {
          setError('images', 'Images must be square.');
          return false;
        }
      }
      return true;
    }

    function validateOptionalHttpUrl(name) {
      const value = fieldValue(name);
      if (!value) return true;
      try {
        const url = new URL(value);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
          setError(name, 'Enter a valid http(s) URL.');
          return false;
        }
      } catch {
        setError(name, 'Enter a valid http(s) URL.');
        return false;
      }
      return true;
    }

    async function validateStep(n) {
      clearErrors();
      let ok = true;

      if (n === 1) {
        const title = fieldValue('title');
        const pitch = fieldValue('pitch');
        if (!title) {
          setError('title', 'Enter a title.');
          ok = false;
        } else if (title.length > 120) {
          setError('title', 'Title must be 120 characters or fewer.');
          ok = false;
        }
        if (!pitch) {
          setError('pitch', 'Enter a pitch.');
          ok = false;
        } else if (pitch.length > 2000) {
          setError('pitch', 'Pitch must be 2000 characters or fewer.');
          ok = false;
        }
        if (!(await validateImages())) ok = false;
      }

      if (n === 2) {
        const creator = fieldValue('creator_name');
        if (!creator) {
          setError('creator_name', 'Enter a creator name.');
          ok = false;
        } else if (creator.length > 80) {
          setError('creator_name', 'Creator name must be 80 characters or fewer.');
          ok = false;
        }
        if (!validateOptionalHttpUrl('portfolio_url')) ok = false;
        if (!validateOptionalHttpUrl('instagram_url')) ok = false;
      }

      return ok;
    }

    function renderPreviews() {
      if (!previewsEl || !emptyEl || !dropzoneEl) return;
      revokePreviewUrls();
      previewsEl.innerHTML = '';

      const hasImages = images.length > 0;
      emptyEl.classList.toggle('hidden', hasImages);
      previewsEl.classList.toggle('hidden', !hasImages);

      dropzoneEl.classList.toggle('border-dashed', true);
      dropzoneEl.classList.toggle('bg-gray-50', !hasImages);
      dropzoneEl.classList.toggle('bg-white', hasImages);
      dropzoneEl.classList.add('border-gray-300', 'aspect-square');
      dropzoneEl.style.aspectRatio = '1 / 1';

      if (hasImages) {
        if (images.length === 1) {
          previewsEl.className = 'absolute inset-0 p-0';
        } else {
          previewsEl.className =
            'absolute inset-0 grid grid-cols-2 gap-2 p-2 content-start overflow-auto';
        }
      }

      images.forEach((file, index) => {
        const url = URL.createObjectURL(file);
        previewUrls.push(url);
        const wrap = document.createElement('div');
        wrap.className =
          images.length === 1
            ? 'relative h-full w-full overflow-hidden rounded-sm bg-gray-100'
            : 'relative aspect-square rounded-sm overflow-hidden bg-gray-100';
        const img = document.createElement('img');
        img.src = url;
        img.alt = file.name;
        img.className = 'h-full w-full object-cover';
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className =
          'absolute top-1.5 right-1.5 z-10 flex h-7 w-7 items-center justify-center rounded-sm bg-black/70 text-white hover:bg-black transition-colors';
        remove.setAttribute('aria-label', 'Remove image');
        remove.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';
        remove.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          images = images.filter((_, i) => i !== index);
          renderPreviews();
        });
        wrap.appendChild(img);
        wrap.appendChild(remove);
        previewsEl.appendChild(wrap);
      });

      if (hasImages && images.length === 1) {
        const replaceHint = document.createElement('button');
        replaceHint.type = 'button';
        replaceHint.className =
          'absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-sm bg-black/70 px-3 py-1.5 text-xs font-medium text-white hover:bg-black transition-colors';
        replaceHint.textContent = 'Add more images';
        replaceHint.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          openFilePicker();
        });
        previewsEl.appendChild(replaceHint);
      } else if (hasImages && images.length < MAX_IMAGES) {
        const addTile = document.createElement('button');
        addTile.type = 'button';
        addTile.className =
          'aspect-square rounded-sm border-2 border-dashed border-gray-300 bg-gray-50 text-gray-500 flex flex-col items-center justify-center gap-1 hover:border-primary-light hover:bg-primary-lightest transition-colors';
        addTile.setAttribute('aria-label', 'Add more images');
        addTile.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="M12 5v14"/></svg><span class="text-[11px] font-medium">Add</span>';
        addTile.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          openFilePicker();
        });
        previewsEl.appendChild(addTile);
      }
    }

    function fillReview() {
      const map = {
        title: fieldValue('title'),
        pitch: fieldValue('pitch'),
        images: `${images.length} image${images.length === 1 ? '' : 's'}`,
        creator_name: fieldValue('creator_name'),
        email: fieldValue('email') || '—',
        portfolio_url: fieldValue('portfolio_url') || '—',
        instagram_url: fieldValue('instagram_url') || '—',
      };
      Object.entries(map).forEach(([key, value]) => {
        const el = form.querySelector(`[data-review="${key}"]`);
        if (el) el.textContent = value;
      });
    }

    function updateStepIndicator(n) {
      if (!progressEl) return;
      progressEl.querySelectorAll('[data-step-indicator]').forEach((item) => {
        const index = Number(item.getAttribute('data-step-indicator'));
        let state = 'pending';
        if (index < n) state = 'completed';
        else if (index === n) state = 'active';
        item.setAttribute('data-step-state', state);

        const circle = item.querySelector('[data-step-circle]');
        const numberEl = item.querySelector('[data-step-number]');
        const checkEl = item.querySelector('[data-step-check]');
        const labelEl = item.querySelector('[data-step-label]');
        const connector = item.querySelector('[data-step-connector]');

        if (circle) {
          circle.className =
            'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors';
          if (state === 'active' || state === 'completed') {
            circle.classList.add('border-primary-light', 'bg-primary-light', 'text-white');
          } else {
            circle.classList.add('border-gray-200', 'bg-gray-200', 'text-gray-500');
          }
        }

        if (numberEl && checkEl) {
          const showCheck = state === 'completed';
          numberEl.classList.toggle('hidden', showCheck);
          checkEl.classList.toggle('hidden', !showCheck);
        }

        if (labelEl) {
          labelEl.classList.toggle('text-contrast-highest', state === 'active');
          labelEl.classList.toggle('text-primary-light', state === 'completed');
          labelEl.classList.toggle('text-contrast-medium', state === 'pending');
        }

        if (connector) {
          connector.classList.toggle('bg-primary-light', index < n);
          connector.classList.toggle('bg-gray-200', index >= n);
        }
      });
    }

    function showStep(n) {
      step = n;
      form.querySelectorAll('[data-step]').forEach((el) => {
        const isActive = Number(el.getAttribute('data-step')) === n;
        el.classList.toggle('hidden', !isActive);
      });
      updateStepIndicator(n);
      if (backBtn) backBtn.classList.toggle('hidden', n === 1);
      if (continueBtn) continueBtn.classList.toggle('hidden', n === TOTAL_STEPS);
      if (submitBtn) {
        const showSubmit = n === TOTAL_STEPS;
        submitBtn.classList.toggle('hidden', !showSubmit);
        submitBtn.classList.toggle('inline-flex', showSubmit);
      }
      if (n === TOTAL_STEPS) fillReview();
    }

    function firstInvalidStep(fields) {
      const order = ['title', 'pitch', 'images', 'creator_name', 'portfolio_url', 'instagram_url'];
      for (const key of order) {
        if (fields[key]) {
          if (key === 'title' || key === 'pitch' || key === 'images') return 1;
          if (key === 'creator_name' || key === 'portfolio_url' || key === 'instagram_url') return 2;
        }
      }
      return TOTAL_STEPS;
    }

    function openFilePicker() {
      if (images.length >= MAX_IMAGES) return;
      fileInput?.click();
    }

    if (fileInput) {
      fileInput.addEventListener('change', () => {
        addImageFiles(fileInput.files);
        fileInput.value = '';
      });
    }

    if (dropzoneEl) {
      dropzoneEl.addEventListener('click', (event) => {
        if (event.target.closest('button')) return;
        openFilePicker();
      });

      dropzoneEl.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openFilePicker();
        }
      });

      dropzoneEl.addEventListener('dragenter', (event) => {
        event.preventDefault();
        dragDepth += 1;
        setDropzoneActive(true);
      });

      dropzoneEl.addEventListener('dragover', (event) => {
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
        setDropzoneActive(true);
      });

      dropzoneEl.addEventListener('dragleave', (event) => {
        event.preventDefault();
        dragDepth = Math.max(0, dragDepth - 1);
        if (dragDepth === 0) setDropzoneActive(false);
      });

      dropzoneEl.addEventListener('drop', (event) => {
        event.preventDefault();
        dragDepth = 0;
        setDropzoneActive(false);
        addImageFiles(event.dataTransfer?.files);
      });
    }

    if (backBtn) {
      backBtn.addEventListener('click', () => {
        if (step > 1) showStep(step - 1);
      });
    }

    if (continueBtn) {
      continueBtn.addEventListener('click', async () => {
        if (!(await validateStep(step))) return;
        if (step < TOTAL_STEPS) showStep(step + 1);
      });
    }

    function setSubmitting(isSubmitting) {
      if (submitBtn) submitBtn.disabled = isSubmitting;
      if (continueBtn) continueBtn.disabled = isSubmitting;
      if (backBtn) backBtn.disabled = isSubmitting;
      if (submitSpinner) {
        submitSpinner.classList.toggle('hidden', !isSubmitting);
      }
      if (submitLabel) {
        submitLabel.textContent = isSubmitting ? 'Submitting…' : 'Submit';
      }
      if (submitBtn) {
        submitBtn.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
      }
    }

    function showResult({ ok, title, message }) {
      form.classList.add('hidden');
      if (!successEl) return;
      successEl.classList.remove('hidden');
      successEl.classList.add('flex');
      if (resultIconSuccess) resultIconSuccess.classList.toggle('hidden', !ok);
      if (resultIconError) resultIconError.classList.toggle('hidden', ok);
      if (resultTitle) {
        resultTitle.textContent = title;
        resultTitle.classList.toggle('text-success', ok);
        resultTitle.classList.toggle('text-error', !ok);
        resultTitle.classList.toggle('text-contrast-highest', false);
      }
      if (resultMessage) {
        if (message) {
          resultMessage.textContent = message;
          resultMessage.classList.remove('hidden');
        } else {
          resultMessage.textContent = '';
          resultMessage.classList.add('hidden');
        }
      }
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      for (const n of [1, 2]) {
        if (!(await validateStep(n))) {
          showStep(n);
          return;
        }
      }
      clearErrors();
      setSubmitting(true);

      const formData = new FormData();
      formData.append('title', fieldValue('title'));
      formData.append('pitch', fieldValue('pitch'));
      formData.append('creator_name', fieldValue('creator_name'));
      const portfolio = fieldValue('portfolio_url');
      if (portfolio) formData.append('portfolio_url', portfolio);
      const instagram = fieldValue('instagram_url');
      if (instagram) formData.append('instagram_url', instagram);
      images.forEach((file) => formData.append('images', file));

      try {
        const response = await fetch(config.apiUrl, {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
        });
        const payload = await response.json().catch(() => ({}));

        if (response.ok && payload && payload.id) {
          showResult({
            ok: true,
            title: 'We received your concept. Our team will review it.',
          });
          return;
        }

        if (response.status === 400 && payload.fields) {
          Object.entries(payload.fields).forEach(([key, message]) => setError(key, message));
          showStep(firstInvalidStep(payload.fields));
          return;
        }

        showResult({
          ok: false,
          title: "Couldn't save your concept",
          message: payload.error || 'Please try again in a moment.',
        });
      } catch {
        showResult({
          ok: false,
          title: "Couldn't save your concept",
          message: 'Please try again in a moment.',
        });
      } finally {
        setSubmitting(false);
      }
    });

    renderPreviews();
    showStep(1);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
